import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import {
  catchError,
  EMPTY,
  forkJoin,
  Observable,
  of,
  Subject,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs';
import { ICRoundService } from 'src/app/image-capture/ic-round.service';
import { AppStateService } from 'src/app/services/app-state.service';
import { ConfigurationService } from 'src/app/services/configuration.service';
import { FeatureFlags } from 'src/app/services/configuration.service.model';
import { ICInstrumentGroupService } from 'src/app/services/image-capture/ic-instrument-group.service';
import { ICInstrument, ICInstrumentGroup } from 'src/app/services/image-capture/ic.service.model';
import { LabpartnerService } from 'src/app/services/labpartner.service';
import {
  ElasticResponseItem,
  Experiment,
  ICCassetteInfo,
  ICExperiment,
  ICExperimentStatus,
  ICImageAnalysisStatusResponse,
  ICImageStatus,
  ICInstrumentOrder,
  ICInstrumentOrderReplacement,
  ICLegendData,
  ICLegendDataIteration,
  ICLegendInstruction,
} from 'src/app/services/labpartner.service.model';
import {
  EVENT_IC_UPDATE_EXPERIMENT_INSTRUCTIONS_AND_INSTRUMENTS,
  EVENT_IC_UPDATE_EXPERIMENT_LEGENDFILE,
  EVENT_IC_UPDATE_EXPERIMENT_TEMPLATE_FILES,
} from 'src/app/services/logging-constants';
import { LoggingService } from 'src/app/services/logging.service';
import { NotesService } from 'src/app/services/notes-service.service';
import { UserAccountService } from 'src/app/services/user-account.service';
import { DialogService } from 'src/app/shared/dialog.service';
import { ExperimentService } from 'src/app/shared/experiment.service';
import { NotificationService } from 'src/app/shared/notification.service';
import { BaseComponent } from 'src/app/support/base.component';
import * as XLSX from 'xlsx';
import {
  ICAdjustLegendComponent,
  ICAdjustLegendComponentData,
} from '../adjust-legend/ic-adjust-legend.component';
import {
  ICSyncDeviceComponent,
  ICSyncDeviceComponentData,
} from '../sync-device/ic-sync-device.component';
import {
  ReplaceInstrumentComponent,
  ReplaceInstrumentComponentData,
} from './replace-instrument/replace-instrument.component';

@Component({
  selector: 'app-experiment-detail',
  templateUrl: './ic-experiment-detail.component.html',
  styleUrls: ['./ic-experiment-detail.component.scss'],
  standalone: false,
})
export class ICExperimentDetailComponent extends BaseComponent implements OnInit, OnDestroy {
  selectedIndex: number = 0;

  currentExperiment: ICExperiment | null = null;

  experimentId: number = 0;
  instrumentGroups: ICInstrumentGroup[] = [];

  provideChannelData: boolean = false;

  incubation: number = 1;
  delay: number = 1;
  numberOfImages: number = 1;

  selectedInstruments: ICInstrument[] = [];

  instrumentOrders: ICInstrumentOrder[] = [];

  customSerialNumber: string = '';

  lutFiles: string[] = [];

  legendFileName: string = '';
  legendFile: File | null = null;

  templateFileNames: string[] = [];
  templateFiles: File[] = [];

  instruments: ICInstrument[] = [];

  legendData: ICLegendData[] = [];

  legendInstructions: ICLegendInstruction[] = [];

  legendDataIterations: ICLegendDataIteration[] = [];
  allLegendDataIterations: ICLegendDataIteration[] = [];

  currentImages: number = 0;
  totalImages: number = 0;
  interval: NodeJS.Timeout | null = null;
  processingInterval: NodeJS.Timeout | null = null;

  successImagesProcessed: number = 0;
  errorImagesProcessed: number = 0;
  totalImagesProcessed: number = 0;
  inProgressImagesProcessed: number = 0;
  excludedImagesProcessed: number = 0;

  lastUpdateTime: Date = new Date();

  DEFAULT_UPDATE_INTERVAL: number = 10000; // Default update interval in milliseconds

  testResults: ElasticResponseItem[] = [];

  fusionLink: string = '';

  cassetteInfo: ICCassetteInfo[] = [];

  currentRoles: string[] = [];

  private readonly _destroying$ = new Subject<void>();

  imageAnalysisResponse: ICImageAnalysisStatusResponse | null = null;

  featureFlags$: Observable<FeatureFlags | null>;

  @ViewChild('firstTab', { read: ElementRef }) firstTab!: ElementRef;

  constructor(
    public service: ExperimentService,
    public accountService: UserAccountService,
    public apiService: LabpartnerService,
    public notesService: NotesService,
    private route: ActivatedRoute,
    private appState: AppStateService,
    private loggingService: LoggingService,
    private instrumentGroupService: ICInstrumentGroupService,
    private notificationService: NotificationService,
    private matDialogService: MatDialog,
    private dialog: MatDialog,
    private configService: ConfigurationService,
    private dialogService: DialogService,
    public icRoundService: ICRoundService,
  ) {
    super();
    this.featureFlags$ = this.configService.featureFlags$;
  }

  protected ngOnDestroyInternal(): void {
    if (this.interval) {
      clearInterval(this.interval as any);
    }
    if (this.processingInterval) {
      clearInterval(this.processingInterval as any);
    }
  }

  loadData(setRound: boolean = false): Observable<any> {
    const obs = forkJoin([
      this.apiService.getICExperiment(this.experimentId),
      this.instrumentGroupService.getInstrumentGroups(),
    ]);
    this.subscription.add(
      obs.subscribe(([experiment, instrumentGroups]) => {
        this.fusionLink = this.configService.geticFusionLink(experiment.experimentId);

        this.templateFiles = []; // Reset the template files
        this.legendFile = null; // Reset the legend file
        this.currentExperiment = experiment;
        this.legendData = JSON.parse(
          JSON.stringify(this.currentExperiment.legendData),
        ) as ICLegendData[];
        // Find if any legend data has channel data
        this.provideChannelData = this.legendData.some(
          (data: ICLegendData) => data.legendChannelData && data.legendChannelData.length > 0,
        );

        console.log('legendData', this.legendData);
        this.templateFileNames =
          this.currentExperiment.templateFiles?.map(file => file.fileName) ?? [];
        this.legendFileName = this.currentExperiment.legendFile?.fileName ?? '';
        this.incubation = experiment.incubation!;
        this.delay = experiment.delay!;
        this.numberOfImages = experiment.numberOfImages!;
        this.appState.SetCurrentExperiment(experiment as Experiment);
        this.instrumentGroups = instrumentGroups;
        this.cassetteInfo = this.currentExperiment?.cassetteInfo ?? [];

        if (setRound) {
          this.icRoundService.setRoundInformation(
            experiment.roundNumber || 1,
            experiment.roundNumber || 1,
          );
          this.setRoundInformation();
          this.subscription.add(
            this.icRoundService.currentRound$.subscribe(() => {
              this.setRoundInformation();
              setTimeout(() => {
                this.firstTab.nativeElement.click();
              });
            }),
          );
        }

        this.setRoundInformation();
      }),
    );
    return obs;
  }

  setLUTFiles() {
    this.instrumentGroups.forEach((group: ICInstrumentGroup) => {
      group.instruments.forEach((instrument: ICInstrument) => {
        instrument.hasLUTFile = this.lutFiles.includes(instrument.instrumentSerial);
      });
    });
    this.selectedInstruments.forEach((instrument: ICInstrument) => {
      instrument.hasLUTFile = this.lutFiles.includes(instrument.instrumentSerial);
    });
  }

  ngOnInit() {
    this.accountService.currentRoles$.pipe(takeUntil(this._destroying$)).subscribe(roles => {
      this.currentRoles = roles;
    });

    this.experimentId = this.route.snapshot.params['id'] as number;

    this.loadData(true);
  }

  validateLegendFile(ref: any) {
    if (
      this.legendInstructions &&
      this.legendInstructions.length > 0 &&
      this.legendInstructions[0].legendDataId
    ) {
      this.dialogService
        .openConfirmDialog(
          'The sequence order is going to be deleted because another legend file is being uploaded. Would you like to continue?',
          { isOk: false, isV2: true, title: 'Replace LegendFile', width: '400px' },
        )
        .afterClosed()
        .subscribe((result: any) => {
          if (!result) {
            this.notificationService.warn('LegendFile upload cancelled.');
            return;
          }
          ref.click();
        });
    } else {
      ref.click();
    }
  }

  onLegendFileUpload(event: any) {
    const file = event.target.files[0];

    // Validate the file type before processing
    if (
      !file ||
      file.type !== 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ) {
      this.notificationService.error('Please upload a valid XLSX file.');
      event.target.value = null;
      return;
    }

    const reader = new FileReader();
    reader.onload = (e: any) => {
      const data = new Uint8Array(e.target.result);
      const workbook = XLSX.read(data, { type: 'array' });

      /** ---------------- VALIDATE FIRST SHEET (LegendData) ---------------- **/
      const firstSheetName = workbook.SheetNames[0];
      const firstSheet = workbook.Sheets[firstSheetName];
      const firstSheetData = XLSX.utils.sheet_to_json(firstSheet, { header: 1 });

      // Ensure the sheet has at least one row of data
      if (!firstSheetData || firstSheetData.length < 2) {
        this.notificationService.error('The first sheet must contain at least one record.');
        event.target.value = null;
        return;
      }

      // Check required columns
      const firstSheetHeaders = firstSheetData[0] as string[];
      if (!firstSheetHeaders.includes('LegendId') || !firstSheetHeaders.includes('Name')) {
        this.notificationService.error(
          'The first sheet must contain "LegendId" and "Name" columns.',
        );
        event.target.value = null;
        return;
      }

      const legendCustomHeaders = firstSheetHeaders.slice(2); // Columns after LegendId and Name
      const legendData: any[] = [];

      // Iterate over each row starting from the second (skip headers)
      for (let i = 1; i < firstSheetData.length; i++) {
        const rowNumber = i + 1; // Excel rows start at 1
        const row: any = firstSheetData[i];

        // Stop processing if the entire row is empty
        if (row.every((cell: any) => cell == null || cell.toString().trim() === '')) {
          event.target.value = null;
          break;
        }

        const [legendId, name] = row;

        // Validate required fields
        if (legendId == null || legendId === '') {
          this.notificationService.error(`Row ${rowNumber}: "LegendId" is required.`);
          event.target.value = null;
          return; // Stop processing on first error
        }

        if (!name || name.toString().trim() === '') {
          this.notificationService.error(`Row ${rowNumber}: "Name" cannot be empty.`);
          event.target.value = null;
          return; // Stop processing on first error
        }

        // Map the remaining columns into the ImportData JSON object
        const importData: any = {};
        legendCustomHeaders.forEach((header: string, idx: number) => {
          importData[header] = row[idx + 2] ?? null;
        });

        legendData.push({
          legendId,
          name: name.toString().trim(),
          importData: JSON.stringify(importData),
        });
      }

      /** ---------------- VALIDATE SECOND SHEET (ChannelData) ---------------- **/
      let channelData: any[] = [];
      if (this.provideChannelData) {
        const secondSheetName = workbook.SheetNames[1];

        // Check that the sheet exists and is named correctly
        if (!secondSheetName || secondSheetName.toLowerCase() !== 'channeldata') {
          this.notificationService.error(
            'The second sheet must be named "ChannelData" (case insensitive).',
          );
          event.target.value = null;
          return;
        }

        const secondSheet = workbook.Sheets[secondSheetName];
        const secondSheetData = XLSX.utils.sheet_to_json(secondSheet, { header: 1 });

        // Ensure the sheet has at least one row of data
        if (!secondSheetData || secondSheetData.length < 2) {
          this.notificationService.error(
            'The "ChannelData" sheet must contain at least one record.',
          );
          event.target.value = null;
          return;
        }

        // Check required columns
        const secondSheetHeaders = secondSheetData[0] as string[];
        if (
          !secondSheetHeaders.includes('LegendId') ||
          !secondSheetHeaders.includes('Channel') ||
          !secondSheetHeaders.includes('Region')
        ) {
          this.notificationService.error(
            'The "ChannelData" sheet must contain "LegendId", "Channel" and "Region" columns.',
          );
          event.target.value = null;
          return;
        }

        const channelCustomHeaders = secondSheetHeaders.slice(3);

        // Helpers for composite key normalization and duplicate tracking
        const normalizeKey = (legendId: any, channel: any, region: any) => {
          const lid = (legendId ?? '').toString().trim();
          const ch = (channel ?? '').toString().trim().toLowerCase();
          const rg = (region ?? '').toString().trim().toLowerCase();
          return `${lid}||${ch}||${rg}`;
        };
        const seen = new Map<string, number>(); // key -> first row number where it was seen
        const duplicates: Array<{ key: string; firstRow: number; dupRow: number }> = [];

        // Iterate over each row starting from the second (skip headers)
        for (let i = 1; i < secondSheetData.length; i++) {
          const rowNumber = i + 1;
          const row = secondSheetData[i] as string[];

          // Stop processing if the entire row is empty
          if (row.every((cell: any) => cell == null || cell.toString().trim() === '')) {
            event.target.value = null;
            break;
          }

          let [legendId, channel, region] = row;

          // Validate required fields
          if (legendId == null || legendId === '') {
            this.notificationService.error(`ChannelData Row ${rowNumber}: "LegendId" is required.`);
            event.target.value = null;
            return; // Stop processing on first error
          }

          if (!channel || channel.toString().trim() === '') {
            this.notificationService.error(
              `ChannelData Row ${rowNumber}: "Channel" cannot be empty.`,
            );
            event.target.value = null;
            return; // Stop processing on first error
          }

          if (!region || region.toString().trim() === '') {
            this.notificationService.error(
              `ChannelData Row ${rowNumber}: "Region" cannot be empty.`,
            );
            event.target.value = null;
            return; // Stop processing on first error
          }

          // Detect duplicates on (LegendId, Channel, Region)
          const key = normalizeKey(legendId, channel, region);
          if (seen.has(key)) {
            duplicates.push({ key, firstRow: seen.get(key)!, dupRow: rowNumber });
          } else {
            seen.set(key, rowNumber);
          }

          // Map the remaining columns into the ImportChannelData JSON object
          const importData: any = {};
          channelCustomHeaders.forEach((header: string, idx: number) => {
            importData[header] = row[idx + 3] ?? null;
          });

          channelData.push({
            legendId,
            channel: channel.toString().trim(),
            region: region.toString().trim(),
            importChannelData: JSON.stringify(importData),
          });
        }

        // If duplicates found, show warning and DO NOT persist state (allow re-upload)
        if (duplicates.length > 0) {
          const numDuplicatesToShow = Math.min(3, duplicates.length);
          const sample = duplicates
            .slice(0, numDuplicatesToShow)
            .map(d => {
              const [lid, ch, rg] = d.key.split('||');
              return `Rows ${d.firstRow} & ${d.dupRow} -> LegendId=${lid}, Channel=${ch}, Region=${rg}`;
            })
            .join('\n');

          // If your notification service does not have 'warn', replace with 'error'
          this.notificationService.warn(
            `ChannelData contains duplicate (LegendId, Channel, Region) combinations.\n` +
            `Please fix and re-upload.\n\nExamples:\n${sample}` +
            (duplicates.length > numDuplicatesToShow
              ? `\n...and ${duplicates.length - numDuplicatesToShow} more.`
              : ''),
          );

          event.target.value = null; // allow the user to re-upload
          return;
        }
      }

      /** ---------------- STORE DATA ---------------- **/
      // Assign validated legend data
      this.legendData = legendData;

      // Link channel data to each legend if applicable
      this.legendData.forEach((data: ICLegendData) => {
        data.legendChannelData = [];
        if (this.provideChannelData) {
          data.legendChannelData = (channelData || []).filter(cd => cd.legendId === data.legendId);
        }
      });

      // Store file information for upload
      this.legendFileName = file.name;
      this.legendFile = file;
      event.target.value = null;

      // Notify the user that the file is fully validated
      this.notificationService.success('Legend file validated successfully and ready for upload.');
    };

    // Handle file reading errors
    reader.onerror = () => {
      this.notificationService.error('Error reading the Legend file.');
      event.target.value = null;
    };

    // Read the XLSX file as an array buffer
    reader.readAsArrayBuffer(file);
  }

  onTemplateFilesUpload(event: any) {
    this.templateFileNames = []; // Reset the template file names
    const files: File[] = Array.from(event.target.files);

    if (files.length !== 3) {
      this.notificationService.error('Please select exactly 3 files.');
      return;
    }

    const jsonFile = files.find(file => file.type === 'application/json');
    const rawFiles = files.filter(file => file.name.endsWith('.raw'));

    if (!jsonFile) {
      this.notificationService.error('A JSON file is required.');
      return;
    }

    if (rawFiles.length !== 2) {
      this.notificationService.error('Exactly 2 RAW files are required.');
      return;
    }

    this.templateFileNames = files.map(file => file.name);
    this.templateFiles = files; // Store the file references
    this.notificationService.success('Template files loaded successfully.');
    event.target.value = null; // Reset the file input value
  }

  onSelectGroup(group: ICInstrumentGroup) {
    this.notificationService.info(`${group.name} Instrument Group Added`);
    group.instruments.forEach((instrument: ICInstrument) => {
      this.onSelectItem(instrument, false);
    });
  }

  onSelectItem(instrument: ICInstrument, showNotification: boolean = true) {
    if (showNotification) {
      this.notificationService.info(`${instrument.instrumentSerial} Instrument Added`);
    }
    const instrumentIndex = this.selectedInstruments.findIndex(
      i => i.instrumentId == instrument.instrumentId,
    );
    if (instrumentIndex == -1) {
      this.selectedInstruments.push(instrument);
    }
  }

  onAddInstrument() {
    // Check if the custom serial number is empty
    if (this.customSerialNumber == '') {
      this.notificationService.warn('Please enter a serial number');
      return;
    }

    // Check if the instrument already exists in the selected instruments
    const existingInstrument = this.selectedInstruments.find(
      i => i.instrumentSerial === this.customSerialNumber,
    );
    if (existingInstrument) {
      this.notificationService.warn(`${this.customSerialNumber} Instrument Already Added`);
      this.customSerialNumber = ''; // Clear the custom serial number after adding
      return;
    }

    // Check if the custom serial number is already in use
    for (const group of this.instrumentGroups) {
      const foundInstrument = group.instruments.find(
        i => i.instrumentSerial === this.customSerialNumber,
      );
      if (foundInstrument) {
        this.onSelectItem(foundInstrument);
        this.customSerialNumber = ''; // Clear the custom serial number after adding
        return;
      }
    }

    this.apiService.getOrAddInstrumentBySerial(this.customSerialNumber).subscribe(
      (instrument: ICInstrument) => {
        this.selectedInstruments.push(instrument);
        this.notificationService.info(`${instrument.instrumentSerial} Instrument Added`);
        this.customSerialNumber = '';
      },
      () => {
        this.notificationService.warn(`Instrument does not exist`);
        this.customSerialNumber = '';
      },
    );
    // Uncomment the following code if you want to allow adding a custom serial number
    // let instrument: ICInstrument = {
    //     instrumentId: 0,
    //     instrumentSerial: this.customSerialNumber,
    //     hasLUTFile: this.lutFiles.includes(this.customSerialNumber)
    // };
    // this.selectedInstruments.push(instrument);
    // this.notificationService.info(`${instrument.instrumentSerial} Instrument Added`);
    // this.customSerialNumber = ''; // Clear the custom serial number after adding
  }

  removeInstrument(instrument: any) {
    this.notificationService.info(`${instrument.instrumentSerial} Instrument Removed`);
    const instrumentIndex = this.selectedInstruments.findIndex(
      i =>
        i.instrumentId == instrument.instrumentId &&
        i.instrumentSerial == instrument.instrumentSerial,
    );
    if (instrumentIndex != -1) {
      this.selectedInstruments.splice(instrumentIndex, 1);
    }
  }

  syncDevice(instrument: any) {
    this.featureFlags$.subscribe(flags => {
      // Check if the feature flag for device sync is enabled
      if (flags?.EnableImageCaptureRequestDeviceSync) {
        this.subscription.add(
          this.apiService.icSyncDevice(instrument.instrumentSerial).subscribe(
            () => {
              const dialogConfig: MatDialogConfig<ICSyncDeviceComponentData> = {
                disableClose: false,
                autoFocus: true,
                width: '60%',
                data: {
                  instrumentSerial: instrument.instrumentSerial,
                },
              };
              const dialogRef = this.matDialogService.open<
                ICSyncDeviceComponent,
                ICSyncDeviceComponentData,
                string[]
              >(ICSyncDeviceComponent, dialogConfig);
              this.subscription.add(
                dialogRef.afterClosed().subscribe((dialogResult: string[] | undefined) => {
                  if (!dialogResult || dialogResult.length === 0) {
                    return;
                  }
                  this.lutFiles = dialogResult;
                  this.setLUTFiles();
                }),
              );
            },
            () => {
              this.notificationService.error('Error syncing device. Please try again later.');
            },
          ),
        );
      } else {
        const link = `<a target="_blank" href='${this.configService.getConfig().lutFileFormURl}'>form</a>`;
        this.subscription.add(
          this.dialogService
            .openConfirmDialog(
              `The LUT (Look Up Table) file for this instrument does not exist in the cloud. The Assay Tools Team needs to sync them manually for the time being, please fill out this ${link} to request the LUT file upload for this instrument.
            `,
              { isOk: true, isV2: true, title: 'LUT', width: '800px' },
            )
            .afterClosed()
            .subscribe(() => { }),
        );
      }
    });
  }

  save() {
    if (this.isSaveDisabled() || this.currentExperiment == null) {
      this.notificationService.warn('Please fill all required fields before saving.');
      return;
    }

    const currentExperiment = JSON.parse(JSON.stringify(this.currentExperiment)) as ICExperiment;
    currentExperiment.incubation = this.incubation;
    currentExperiment.delay = this.delay;
    currentExperiment.numberOfImages = this.numberOfImages;
    currentExperiment.legendData = this.legendData;

    currentExperiment.instrumentOrders = this.selectedInstruments.map(i => {
      return {
        instrumentId:
          this.selectedInstruments.find(ins => ins.instrumentSerial == i.instrumentSerial)
            ?.instrumentId || 0,
      };
    });

    // Compare current and new instrument orders
    const currentInstrumentOrders = this.currentExperiment.instrumentOrders || [];
    const newInstrumentOrders = currentExperiment.instrumentOrders || [];

    const isInstrumentOrdersDifferent =
      currentInstrumentOrders.length !== newInstrumentOrders.length ||
      currentInstrumentOrders.some(
        (order, index) => order.instrumentId !== newInstrumentOrders[index].instrumentId,
      );

    // Check if the current experiment is the same as the new one
    if (
      this.currentExperiment.incubation == this.incubation &&
      this.currentExperiment.delay == this.delay &&
      this.currentExperiment.numberOfImages == this.numberOfImages &&
      this.currentExperiment.instrumentOrders?.length == this.selectedInstruments.length &&
      !isInstrumentOrdersDifferent &&
      this.legendFile == null &&
      this.templateFiles.length == 0
    ) {
      this.notificationService.warn('No changes detected. Please make changes before saving.');
      return;
    }

    // Create a FormData object for the template files
    const templateFilesFormData = new FormData();
    if (this.templateFiles.length > 0) {
      templateFilesFormData.append('experimentId', this.experimentId.toString());
      templateFilesFormData.append(
        'icExperimentId',
        currentExperiment.icExperimentId?.toString() ?? '',
      );
      this.templateFiles.forEach(file => {
        templateFilesFormData.append('templateFiles', file, file.name);
      });

      const props: { [key: string]: number } = {
        ExperimentId: this.currentExperiment?.experimentId,
        ICExperimentId: this.currentExperiment?.icExperimentId
          ? this.currentExperiment?.icExperimentId
          : 0,
        TemplateFiles: this.templateFiles.length,
        ExperimentOwner: this.currentExperiment?.createdBy ?? 0,
      };
      this.loggingService.logEvent(EVENT_IC_UPDATE_EXPERIMENT_TEMPLATE_FILES, props);
    }

    // Create a FormData object for the legend file
    const legendFileFormData = new FormData();
    if (this.legendFile) {
      legendFileFormData.append('experimentId', this.experimentId.toString());
      legendFileFormData.append(
        'icExperimentId',
        currentExperiment.icExperimentId?.toString() ?? '',
      );
      legendFileFormData.append('legendFile', this.legendFile, this.legendFileName);

      const props: { [key: string]: number } = {
        ExperimentId: this.currentExperiment?.experimentId,
        ICExperimentId: this.currentExperiment?.icExperimentId
          ? this.currentExperiment?.icExperimentId
          : 0,
        LegendDataCount: this.legendData.length,
        ExperimentOwner: this.currentExperiment?.createdBy ?? 0,
      };
      this.loggingService.logEvent(EVENT_IC_UPDATE_EXPERIMENT_LEGENDFILE, props);
    }

    const props: { [key: string]: number } = {
      ExperimentId: this.currentExperiment?.experimentId,
      ICExperimentId: this.currentExperiment?.icExperimentId
        ? this.currentExperiment?.icExperimentId
        : 0,
      Incubation: this.incubation,
      Delay: this.delay,
      NumberOfImages: this.numberOfImages,
      InstrumentOrdersCount: this.selectedInstruments.length,
      ExperimentOwner: this.currentExperiment?.createdBy ?? 0,
    };
    this.loggingService.logEvent(EVENT_IC_UPDATE_EXPERIMENT_INSTRUCTIONS_AND_INSTRUMENTS, props);

    this.subscription.add(
      forkJoin([
        this.apiService.updateICExperimentInputs(this.experimentId, currentExperiment, '', {}),
        this.templateFiles.length > 0
          ? this.apiService.uploadTemplateFiles(templateFilesFormData)
          : of(null),
        this.legendFile ? this.apiService.uploadLegendFile(legendFileFormData) : of(null),
      ]).subscribe(
        () => {
          this.loadData();
          this.notificationService.success('Experiment saved successfully.');
        },
        () => {
          this.notificationService.error('Error saving experiment. Please try again later.');
        },
      ),
    );
  }

  isSaveDisabled() {
    return (
      this.incubation < 0 ||
      (this.incubation > 0 && this.incubation < 10) ||
      (!this.incubation && this.incubation !== 0) ||
      this.delay < 0 ||
      (!this.delay && this.delay !== 0) ||
      this.numberOfImages < 0 ||
      (!this.numberOfImages && this.numberOfImages !== 0) ||
      this.selectedInstruments.some(i => i.hasLUTFile == false) ||
      this.selectedInstruments.length == 0 ||
      !this.legendFileName ||
      this.templateFileNames.length != 3
    );
  }

  parseExperiment(): Experiment {
    return this.currentExperiment as Experiment;
  }

  removeTemplateFiles() {
    this.templateFiles = [];
    this.templateFileNames = [];
    this.notificationService.info('Template files removed successfully.');
  }

  removeLegendFile() {
    if (
      this.legendInstructions &&
      this.legendInstructions.length > 0 &&
      this.legendInstructions[0].legendDataId
    ) {
      // Check if the legend file is already loaded
      this.dialogService
        .openConfirmDialog(
          'The sequence order is going to be deleted because you are removing the legend file, would you like to continue?',
          { isOk: false, isV2: true, title: 'Remove LegendFile', width: '400px' },
        )
        .afterClosed()
        .subscribe((result: any) => {
          if (!result) {
            this.notificationService.warn('Legend file removal cancelled.');
            return;
          } else {
            this.legendFile = null;
            this.legendFileName = '';
            this.notificationService.info('Legend file removed successfully.');
          }
        });
    } else {
      this.legendFile = null;
      this.legendFileName = '';
      this.notificationService.info('Legend file removed successfully.');
    }
  }

  onChangeSetReady(event: any) {
    console.log('onChangeSetReady', event);
    if (this.currentExperiment) {
      this.loadData().subscribe(() => {
        this.selectedIndex = 1;
      });
    }
  }

  onChangeSequenceSaved() {
    if (this.currentExperiment) {
      this.loadData().subscribe(() => { });
    }
  }

  areAllImagesAnalyzed(): boolean {
    return (this.currentImages * 100) / this.totalImages >= 100;
  }

  onTabChange(tabIndex: any) {
    // Clear any existing intervals before starting new ones
    if (this.interval != null) {
      clearInterval(this.interval as any);
      this.interval = null;
    }
    if (this.processingInterval != null) {
      clearInterval(this.processingInterval as any);
      this.processingInterval = null;
    }

    if (tabIndex.index == 1) {
      console.log('checkInProgress');
      this.getTestResults(false);

      if (this.interval == null) {
        this.interval = setInterval(() => {
          this.getTestResults(true);
        }, this.DEFAULT_UPDATE_INTERVAL);
      }
    }

    if (tabIndex.index == 2) {
      this.getImageAnalysis(false);
      if (this.processingInterval == null) {
        this.processingInterval = setInterval(() => {
          this.getImageAnalysis(true);
        }, this.DEFAULT_UPDATE_INTERVAL);
      }
    }
  }

  analyzeImages() {
    this.testResults.forEach((result: ElasticResponseItem) => {
      result.isExtra = false; // Reset isExtra for all results
    });

    this.dialog
      .open<ICAdjustLegendComponent, ICAdjustLegendComponentData, ElasticResponseItem[]>(
        ICAdjustLegendComponent,
        {
          width: '80%',
          data: {
            legendData: JSON.parse(JSON.stringify(this.legendData)),
            testResults: JSON.parse(JSON.stringify(this.testResults)),
            legendDataIteration: JSON.parse(JSON.stringify(this.allLegendDataIterations)),
            cassetteInfo: JSON.parse(JSON.stringify(this.cassetteInfo)),
            icExperimentId: this.currentExperiment?.icExperimentId || 0,
            experiment: this.currentExperiment!,
          },
        },
      )
      .afterClosed()
      .subscribe((result?: ElasticResponseItem[]) => {
        if (result) {
          this.subscription.add(
            this.apiService
              .setICExperimentAnalyzingImages(
                this.currentExperiment?.icExperimentId!,
                result.filter(r => !r.cassetteInfo?.isExcluded).map(r => r.testResultId),
                this.icRoundService.getCurrentRound(),
              )
              .subscribe(() => {
                this.subscription.add(
                  this.loadData().subscribe(() => {
                    this.notificationService.success('Experiment set to analyzing images.');
                    this.selectedIndex = 2;
                  }),
                );
              }),
          );
        } else {
          this.notificationService.warn(':: Cancelled analyzing images ::');
        }
      });
  }

  updateTime() {
    this.lastUpdateTime = new Date();
  }

  getImageAnalysis(hideLoader: boolean) {
    this.subscription.add(
      this.apiService
        .getImageAnalysisStatus(
          this.currentExperiment!.experimentId,
          this.icRoundService.getCurrentRound(),
          hideLoader,
        )
        .subscribe({
          next: response => {
            this.imageAnalysisResponse = response;
            this.lastUpdateTime = new Date();

            this.successImagesProcessed = response.successCount || 0;
            this.errorImagesProcessed = response.errorCount || 0;
            this.totalImagesProcessed = response.totalCount || 0;
            this.inProgressImagesProcessed = response.inProgressCount || 0;
            this.excludedImagesProcessed = response.excludedCount || 0;

            if (response.status === ICImageStatus.Completed) {
              // If the experiment is in the last round and is in progress, set it to complete
              if (
                this.icRoundService.getIsInLastRound() &&
                this.currentExperiment?.status == ICExperimentStatus.AnalyzingImages
              ) {
                this.subscription.add(
                  this.apiService
                    .setICExperimentComplete(this.currentExperiment.icExperimentId!)
                    .subscribe(() => this.loadData()),
                );
              }

              // If all images are processed and the status is completed stop the polling
              if (response.inProgressCount == 0) {
                if (this.processingInterval) {
                  clearInterval(this.processingInterval as any);
                  this.processingInterval = null;
                }
              }
            }
          },
          error: err => {
            console.error('Error on initial image analysis status check:', err);
            // Still start polling even if the initial call fails
          },
        }),
    );
  }
  getTestResults(hideLoader: boolean) {
    this.updateTime();
    this.subscription.add(
      // Fetch the test results for the current experiment
      this.apiService.getICTestResults(this.currentExperiment?.experimentId!, hideLoader).subscribe(
        response => {
          let currentTestResults: ElasticResponseItem[] = [];
          this.currentImages = 0;
          if (response && response.length > 0) {
            this.testResults = response;
            currentTestResults = this.testResults.filter(
              (result: ElasticResponseItem) =>
                result.imageCapture.roundNumber == this.icRoundService.getCurrentRound(),
            );
          }
          // If there are no test results, return early
          if (currentTestResults.length > 0) {
            // If the current experiment is not in progress, set it to in progress
            if (
              this.icRoundService.getIsInLastRound() &&
              this.currentExperiment?.status == ICExperimentStatus.Ready
            ) {
              this.subscription.add(
                this.apiService
                  .setICExperimentInProgress(this.currentExperiment?.icExperimentId!)
                  .subscribe(
                    () => {
                      this.currentExperiment!.status = ICExperimentStatus.InProgress;
                      this.currentExperiment = JSON.parse(
                        JSON.stringify(this.currentExperiment),
                      ) as ICExperiment;
                    },
                    () => {
                      this.notificationService.error('Error setting experiment as InProgress.');
                    },
                  ),
              );
            }

            // Reset the current images count

            // Update the current images for each selected instrument based on the response
            this.selectedInstruments.forEach((instrument: ICInstrument) => {
              if (instrument.currentImages != null && instrument.totalImages != null) {
                // Find the serial numbers of the instrument and its replacements
                let serialNumbers = [instrument.instrumentSerial];
                if (
                  (instrument as ICInstrumentOrder).replacements &&
                  (instrument as ICInstrumentOrder).replacements!.length > 0
                ) {
                  serialNumbers = serialNumbers.concat(
                    (instrument as ICInstrumentOrder).replacements!.map(
                      r =>
                        this.instruments.find(i => i.instrumentId == r.instrumentId)
                          ?.instrumentSerial || '',
                    ),
                  );
                }

                // Filter the results for the current instrument and its replacements
                const results = currentTestResults.filter((result: ElasticResponseItem) =>
                  serialNumbers.includes(result.imageCapture.instrumentSerial),
                );

                // Update the current images and extra images for the instrument
                instrument.currentImages = results.filter(
                  (result: ElasticResponseItem) =>
                    result.imageCapture.casseteNumber.split('.').length == 1 ||
                    (result.imageCapture.casseteNumber.split('.').length == 2 &&
                      result.imageCapture.casseteNumber.split('.')[1] == '1'),
                ).length;

                instrument.currentExtraImages = results.length - instrument.currentImages;

                this.currentImages += results.length;
              }
            });
          }
        },
        () => {
          if (!hideLoader) {
            this.notificationService.error(
              'Error retrieving test results. Please try again later.',
            );
          }
        },
      ),
    );
  }

  adustLegend() {
    this.testResults.forEach((result: ElasticResponseItem) => {
      result.isExtra = false; // Reset isExtra for all results
    });

    this.subscription.add(
      this.dialog
        .open<ICAdjustLegendComponent, ICAdjustLegendComponentData, void>(ICAdjustLegendComponent, {
          width: '80%',
          data: {
            legendData: JSON.parse(JSON.stringify(this.legendData)),
            testResults: JSON.parse(JSON.stringify(this.testResults)),
            legendDataIteration: JSON.parse(JSON.stringify(this.allLegendDataIterations)),
            cassetteInfo: JSON.parse(JSON.stringify(this.cassetteInfo)),
            icExperimentId: this.currentExperiment?.icExperimentId || 0,
            experiment: this.currentExperiment!,
          },
        })
        .afterClosed()
        .subscribe(() => {
          this.apiService
            .getICExperiment(this.experimentId)
            .subscribe((experiment: ICExperiment) => {
              if (experiment) {
                this.cassetteInfo = experiment.cassetteInfo || [];
              }
            });
        }),
    );
  }

  replaceInstrument(instrumentOrder: ICInstrumentOrder) {
    const serial =
      this.instruments.find(i => i.instrumentId == instrumentOrder.instrumentId)?.instrumentSerial ||
      instrumentOrder.instrumentSerial;

    if (!serial) {
      return;
    }

    this.subscription.add(
      this.dialog
        .open<ReplaceInstrumentComponent, ReplaceInstrumentComponentData, any>(
          ReplaceInstrumentComponent,
          {
            width: '80%',
            data: {
              instrumentGroups: JSON.parse(JSON.stringify(this.instrumentGroups)),
              selectedInstruments: JSON.parse(
                JSON.stringify(
                  ([] as any).map((i: any) => {
                    const instrument = this.instruments.find(
                      ins => ins.instrumentId == i.instrumentId,
                    );
                    return {
                      instrumentId: i.instrumentId,
                      instrumentSerial: instrument?.instrumentSerial,
                      hasLUTFile: this.lutFiles.includes(instrument?.instrumentSerial || ''),
                      currentImages: 0,
                      totalImages: this.legendDataIterations
                        .filter(iteration => iteration.instrumentId == i.instrumentId)
                        .reduce((sum, iteration) => sum + iteration.numberOfImageSets, 0),
                    } as ICInstrument;
                  }),
                ),
              ),
              lutFiles: JSON.parse(JSON.stringify(this.lutFiles)),
              currentInstrumentSerialNumber: serial,
            },
          },
        )
        .afterClosed()
        .subscribe((result: ICInstrument[]) => {
          if (result != null) {
            const replacements: ICInstrumentOrderReplacement[] = [];
            result.forEach((instrument: ICInstrument) => {
              replacements.push({
                instrumentId: instrument.instrumentId,
                instrumentOrderId: instrumentOrder.instrumentOrderId,
              } as ICInstrumentOrderReplacement);
            });

            // Extract all testResultIds from testResults (already loaded in the view)
            const testResultIds = this.testResults
              .map(tr => tr.testResultId)
              .filter((id): id is string => !!id)
              .filter((id, index, self) => self.indexOf(id) === index); // Remove duplicates

            this.subscription.add(
              this.apiService
                .icReplaceInstrument(instrumentOrder.instrumentOrderId!, replacements[0], testResultIds)
                .subscribe(() => {
                  this.notificationService.success('Instrument replaced successfully.');
                  this.apiService
                    .getICExperiment(this.experimentId)
                    .subscribe((experiment: ICExperiment) => {
                      if (experiment) {
                        this.instrumentOrders = experiment.instrumentOrders || [];
                      }
                    });
                  this.loadData();
                }),
            );
          } else {
            this.notificationService.warn(':: Cancelled replacing instrument ::');
          }
        }),
    );
  }

  getInstrumentLabel(i: number) {
    const instrument = this.instruments.find(
      ins => ins.instrumentId == this.instrumentOrders[i]?.instrumentId,
    );
    return instrument
      ? instrument.instrumentSerial
      : this.instrumentOrders[i]?.instrumentSerial || 'Unknown Instrument';
  }

  getInstrumentHistory(instrument: ICInstrumentOrder) {
    if (instrument.replacements && instrument.replacements.length > 0) {
      const originalSerial =
        this.instruments.find(ins => ins.instrumentId == instrument.originalInstrumentId)
          ?.instrumentSerial ||
        instrument.originalInstrumentSerial ||
        'Unknown';

      // Sort replacements to ensure chronological order, then remove the last one (which is the current instrument)
      const sortedReplacements = [...instrument.replacements].sort(
        (a, b) => (a.instrumentOrderReplacementId || 0) - (b.instrumentOrderReplacementId || 0),
      );

      // We want to show history, so we exclude the current active replacement
      const historyReplacements = sortedReplacements.slice(0, -1);

      if (historyReplacements.length === 0) {
        return `(Instrument History: ${originalSerial})`;
      }

      const replacementSerials = historyReplacements
        .map(
          r =>
            this.instruments.find(ins => ins.instrumentId == r.instrumentId)?.instrumentSerial ||
            r.instrumentSerial ||
            'Unknown',
        )
        .join(', ');

      return `(Instrument History: ${originalSerial}, ${replacementSerials})`;
    } else {
      return '';
    }
  }

  downloadReport() {
    if (this.imageAnalysisResponse) {
      this.subscription.add(
        this.apiService
          .downloadImageAnalysisReport(this.imageAnalysisResponse.imageAnalysisId)
          .subscribe(response => {
            if (!response.body) {
              return;
            }

            // Extract the filename from the Content-Disposition header
            const contentDisposition = response.headers.get('Content-Disposition');
            console.log(contentDisposition);
            let filename = 'image-analysis.csv'; // Fallback filename

            if (contentDisposition) {
              filename = contentDisposition;
            }

            // Create a Blob URL and trigger the download
            const url = window.URL.createObjectURL(response.body);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename; // Use the extracted filename
            a.click();
            window.URL.revokeObjectURL(url);
          }),
      );
    }
  }

  // Cancel the experiment
  cancelExperiment() {
    this.subscription.add(
      this.dialogService
        .openConfirmDialog(
          'Are you sure to cancel the experiment? If you cancel it there is no way to recover it to continue working on it?',
          { isOk: false, isV2: true, title: 'Cancel Experiment', width: '400px' },
        )
        .afterClosed()
        .pipe(
          switchMap((result: any) => {
            if (result) {
              // the user confirmed the cancellation
              return this.apiService.icCancelExperiment(this.currentExperiment!.experimentId).pipe(
                tap(() => {
                  this.notificationService.success('Experiment cancelled successfully.');
                  this.loadData();
                }),
                catchError(() => {
                  this.notificationService.error(
                    'Error cancelling experiment. Please try again later.',
                  );
                  return EMPTY;
                }),
              );
            } else {
              // the user closed the modal or clicked "No", just show a warning message
              this.notificationService.warn('Experiment cancellation aborted.');
              return EMPTY; // no API call is made
            }
          }),
        )
        .subscribe(),
    );
  }

  isExpReopened(): boolean {
    return (this.currentExperiment as ICExperiment)?.roundNumber! > 1;
  }

  setRoundInformation() {
    this.legendInstructions = JSON.parse(
      JSON.stringify(
        this.currentExperiment?.legendInstructions?.filter(
          instruction => instruction.roundNumber == this.icRoundService.getCurrentRound(),
        ),
      ),
    ) as ICLegendInstruction[];
    this.allLegendDataIterations = JSON.parse(
      JSON.stringify(this.currentExperiment?.legendDataIterations),
    ) as ICLegendDataIteration[];
    this.legendDataIterations = JSON.parse(
      JSON.stringify(
        this.currentExperiment?.legendDataIterations?.filter(
          iteration => iteration.roundNumber == this.icRoundService.getCurrentRound(),
        ),
      ),
    ) as ICLegendDataIteration[];
    this.totalImages = this.legendDataIterations.reduce(
      (sum, iteration) => sum + iteration.numberOfImageSets,
      0,
    );
    this.instrumentGroups.forEach((group: ICInstrumentGroup) => {
      group.instruments.forEach((instrument: ICInstrument) => {
        if (
          this.instruments.findIndex(i => i.instrumentSerial == instrument.instrumentSerial) == -1
        ) {
          this.instruments.push(instrument);
        }
      });
    });

    this.instrumentOrders = this.currentExperiment?.instrumentOrders || [];
    this.selectedInstruments =
      this.currentExperiment?.instrumentOrders?.map(i => {
        return {
          instrumentOrderId: i.instrumentOrderId || 0,
          instrumentId: i.instrumentId,
          instrumentSerial:
            this.instruments.find(ins => ins.instrumentId == i.instrumentId)?.instrumentSerial ||
            i.instrumentSerial ||
            '',
          hasLUTFile: this.instruments.find(ins => ins.instrumentId == i.instrumentId)?.hasLUTFile || i.hasLUTFile,
          currentImages: 0,
          totalImages: this.legendDataIterations
            .filter(iteration => iteration.instrumentId == i.originalInstrumentId)
            .reduce((sum, iteration) => sum + iteration.numberOfImageSets, 0),
          replacements: i.replacements,
          originalInstrumentId: i.originalInstrumentId || 0,
          originalInstrumentSerial: i.originalInstrumentSerial || '',
        } as ICInstrument;
      }) || [];

    if (this.currentExperiment?.status == ICExperimentStatus.Complete) {
      // Avoid to assign this since we need the real values
    }
  }

  onLoadPreviousSequence() {
    this.legendInstructions = JSON.parse(
      JSON.stringify(
        this.currentExperiment?.legendInstructions?.filter(
          instruction => instruction.roundNumber == this.icRoundService.getCurrentRound() - 1,
        ),
      ),
    ) as ICLegendInstruction[];
  }
}
